'use client'

import AwardIcon from '@/icons/brand/AwardIcon'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'
import {
  Recommendation,
  recommendationLabel,
} from '@/collections/JunctionTables/AppCategoryRecommendation/constants'

const awardVariants = cva('flex items-center', {
  variants: {
    recommendation: {
      [Recommendation.TopPick]: 'text-primary-500',
      [Recommendation.Recommended]: 'text-gray-500',
      [Recommendation.Capable]: 'text-gray-700',
      [Recommendation.NotRecommended]: 'text-gray-700',
    },
    overlay: {
      true: 'text-white p-0 gap-6 bg-transparent',
    },
  },
})

const iconVariants = cva('', {
  variants: {
    recommendation: {
      [Recommendation.TopPick]: '[&>path]:fill-primary-600',
      [Recommendation.Recommended]: '[&>path]:fill-gray-600',
      [Recommendation.Capable]: '[&>path]:fill-gray-600',
      [Recommendation.NotRecommended]: '[&>path]:fill-gray-600',
    },
    overlay: {
      true: '[&>path]:fill-white/40',
    },
  },
})

const containerVariants = cva('-mt-3 flex flex-col items-center', {
  variants: {
    recommendation: {
      [Recommendation.TopPick]: null,
      [Recommendation.Recommended]: null,
      [Recommendation.Capable]: null,
      [Recommendation.NotRecommended]: null,
    },
    overlay: {
      true: 'relative animate-shine bg-gradient-to-r from-white/40 via-white to-white/40 bg-[length:200%_100%] bg-clip-text text-transparent',
    },
  },
})

const textVariants = cva('text-center text-13 leading-tight', {
  variants: {
    recommendation: {
      [Recommendation.TopPick]: 'text-primary-700',
      [Recommendation.Recommended]: 'text-gray-700',
      [Recommendation.Capable]: 'text-gray-700',
      [Recommendation.NotRecommended]: 'text-gray-700',
    },
    overlay: {
      true: 'text-transparent',
    },
  },
})

const rankVariants = cva(
  'flex w-6 h-6 items-center justify-center rounded-full border text-12 font-mono',
  {
    variants: {
      recommendation: {
        [Recommendation.TopPick]: 'bg-primary-500/10 border-primary-500/30 text-primary-200',
        [Recommendation.Recommended]: 'bg-white/10 border-white/30 text-white',
        [Recommendation.Capable]: 'bg-white/10 border-white/30 text-white',
        [Recommendation.NotRecommended]: 'bg-white/10 border-white/30 text-white',
      },
    },
  },
)

type AwardProps = VariantProps<typeof awardVariants> & {
  category?: string
  recommendation: Recommendation
  rank?: number
  overlay?: boolean
  size?: string
  gap?: string
  nested?: boolean
  children?: React.ReactNode
  className?: string
}

export default function Award({
  category,
  recommendation,
  rank,
  overlay,
  size,
  gap,
  nested,
  children,
  className,
}: AwardProps) {
  return (
    <div className={cn(awardVariants({ recommendation, overlay }), gap ?? 'gap-2', className)}>
      {recommendation !== Recommendation.NotRecommended &&
        recommendation !== Recommendation.Capable && (
          <AwardIcon className={cn(iconVariants({ recommendation, overlay }), size)} />
        )}
      {category && (
        <div className={containerVariants({ recommendation, overlay })}>
          <span
            aria-hidden={nested ? true : undefined}
            className="text-14 font-semibold capitalize"
          >
            {recommendationLabel[recommendation]}
          </span>
          <span className={textVariants({ recommendation, overlay })}>{category}</span>
        </div>
      )}
      {children && children}
      {rank && (
        <span className={rankVariants({ recommendation })} aria-hidden={nested ? true : undefined}>
          {rank}
        </span>
      )}
      {recommendation !== Recommendation.NotRecommended &&
        recommendation !== Recommendation.Capable && (
          <AwardIcon
            className={cn(iconVariants({ recommendation, overlay }), size, '-scale-x-100')}
          />
        )}

      {nested && (
        <span className="sr-only">
          {recommendationLabel[recommendation]}
          {category ? ` ${category}` : ''}
          {rank ? `, rank ${rank}` : ''}
        </span>
      )}
    </div>
  )
}
